// components/PasskeyManagement.tsx
// Passkey management UI for listing, adding, renaming, and deleting passkeys
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import LaptopIcon from '@mui/icons-material/Laptop';
import SecurityIcon from '@mui/icons-material/Security';
import { PasskeyService, Passkey } from '../services/PasskeyService';
import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/types';

const PasskeyManagement: React.FC = () => {
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [friendlyName, setFriendlyName] = useState('');
  // Registration options are pre-fetched when the dialog opens so the WebAuthn
  // ceremony can start synchronously from the click — Safari loses user
  // activation if a network request is awaited between the click and create().
  const [registrationOptions, setRegistrationOptions] = useState<PublicKeyCredentialCreationOptionsJSON | null>(null);

  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renamingPasskey, setRenamingPasskey] = useState<Passkey | null>(null);
  const [newFriendlyName, setNewFriendlyName] = useState('');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingPasskey, setDeletingPasskey] = useState<Passkey | null>(null);

  useEffect(() => {
    loadPasskeys();
  }, []);

  const loadPasskeys = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await PasskeyService.listPasskeys();
      setPasskeys(data);
    } catch (err) {
      setError('Failed to load passkeys');
    } finally {
      setLoading(false);
    }
  };

  // Open the Add dialog and pre-fetch registration options so the WebAuthn
  // ceremony can start without an awaited network request on the click (Safari).
  const handleOpenAddDialog = async () => {
    setError(null);
    setFriendlyName('');
    setRegistrationOptions(null);
    setAddDialogOpen(true);

    const result = await PasskeyService.fetchRegistrationOptions();
    if (result.success && result.options) {
      setRegistrationOptions(result.options);
    } else {
      setError(result.message || 'Failed to start registration');
    }
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setRegistrationOptions(null);
  };

  const handleAddPasskey = async () => {
    if (!registrationOptions) return;

    setAdding(true);
    setError(null);

    try {
      // Note: call completeRegistration synchronously off the click — no awaited
      // network request before it — so Safari keeps user activation for create().
      const result = await PasskeyService.completeRegistration(registrationOptions, friendlyName || undefined);

      if (result.success) {
        setSuccessMessage('Passkey added successfully');
        setAddDialogOpen(false);
        setFriendlyName('');
        setRegistrationOptions(null);
        await loadPasskeys();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setAdding(false);
    }
  };

  const handleRenamePasskey = async () => {
    if (!renamingPasskey) return;

    setError(null);
    try {
      const result = await PasskeyService.renamePasskey(renamingPasskey.id, newFriendlyName);

      if (result.success) {
        setSuccessMessage('Passkey renamed successfully');
        setRenameDialogOpen(false);
        setRenamingPasskey(null);
        setNewFriendlyName('');
        await loadPasskeys();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    }
  };

  const handleDeletePasskey = async () => {
    if (!deletingPasskey) return;

    setError(null);
    try {
      const result = await PasskeyService.deletePasskey(deletingPasskey.id);

      if (result.success) {
        setSuccessMessage('Passkey deleted successfully');
        setDeleteDialogOpen(false);
        setDeletingPasskey(null);
        await loadPasskeys();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    }
  };

  const getDeviceIcon = (deviceType: string | null) => {
    if (deviceType === 'platform') {
      return <PhoneAndroidIcon />;
    } else if (deviceType === 'cross-platform') {
      return <SecurityIcon />;
    }
    return <LaptopIcon />;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Passkeys
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Passkeys let you sign in quickly and securely using your fingerprint, face, or device PIN.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<FingerprintIcon />}
          onClick={handleOpenAddDialog}
        >
          Add Passkey
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {passkeys.length === 0 ? (
        <Box textAlign="center" py={4}>
          <FingerprintIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="body1" color="text.secondary" paragraph>
            You haven&apos;t added any passkeys yet.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Add a passkey to sign in faster and more securely.
          </Typography>
          <Button
            variant="contained"
            startIcon={<FingerprintIcon />}
            onClick={handleOpenAddDialog}
          >
            Add Your First Passkey
          </Button>
        </Box>
      ) : (
        <List>
          {passkeys.map((passkey, index) => (
            <ListItem key={passkey.id} divider>
              <Box display="flex" alignItems="center" mr={2}>
                {getDeviceIcon(passkey.device_type)}
              </Box>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle1">
                      {passkey.friendly_name || `Passkey ${index + 1}`}
                    </Typography>
                    {passkey.backup_state && (
                      <Chip label="Synced" size="small" color="primary" variant="outlined" />
                    )}
                  </Box>
                }
                secondary={
                  <>
                    <Typography variant="body2" component="span">
                      Added {formatDate(passkey.created_at)}
                    </Typography>
                    {passkey.last_used_at && (
                      <Typography variant="body2" component="span" display="block">
                        Last used {formatDate(passkey.last_used_at)}
                      </Typography>
                    )}
                  </>
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="rename"
                  onClick={() => {
                    setRenamingPasskey(passkey);
                    setNewFriendlyName(passkey.friendly_name || '');
                    setRenameDialogOpen(true);
                  }}
                  sx={{ mr: 1 }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => {
                    setDeletingPasskey(passkey);
                    setDeleteDialogOpen(true);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      {/* Add Passkey Dialog */}
      <Dialog open={addDialogOpen} onClose={() => !adding && handleCloseAddDialog()} maxWidth="sm" fullWidth>
        <DialogTitle>Add a New Passkey</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            You&apos;ll be prompted to use your fingerprint, face, or device PIN to create a new passkey.
          </Typography>
          <TextField
            fullWidth
            label="Name this passkey (optional)"
            placeholder="e.g., My iPhone, Work Laptop"
            value={friendlyName}
            onChange={(e) => setFriendlyName(e.target.value)}
            margin="normal"
            disabled={adding}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} disabled={adding}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddPasskey}
            disabled={adding || !registrationOptions}
            startIcon={adding ? <CircularProgress size={20} /> : <FingerprintIcon />}
          >
            {adding ? 'Adding...' : 'Add Passkey'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Passkey Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Rename Passkey</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="New name"
            placeholder="e.g., My iPhone, Work Laptop"
            value={newFriendlyName}
            onChange={(e) => setNewFriendlyName(e.target.value)}
            margin="normal"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRenamePasskey}>
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Passkey Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Passkey?</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete &quot;{deletingPasskey?.friendly_name || `Passkey ${passkeys.findIndex(p => p.id === deletingPasskey?.id) + 1}`}&quot;?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            You won&apos;t be able to use this passkey to sign in anymore. You can always add it back later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeletePasskey}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PasskeyManagement;
